import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategoriesGrid from './components/CategoriesGrid';
import ComparisonSection from './components/ComparisonSection';
import GuidesSection from './components/GuidesSection';
import FAQSection from './components/FAQSection';
import AboutSection from './components/AboutSection';
import CTASection from './components/CTASection';
import { faqData } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuyspreadsheet.blog';

const homepageSchemas = {
  website: {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: `${siteUrl}/`,
    name: 'CSSBuy Spreadsheet Brasil',
    description: 'O maior diretório de CSSBuy Spreadsheet do Brasil. Encontre os melhores links, categorias, guias e planilhas CSSBuy atualizadas diariamente.',
    inLanguage: 'pt-BR',
    publisher: { '@id': `${siteUrl}/#organization` },
  },
  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Equipe CSSBuy Spreadsheet Brasil',
    url: `${siteUrl}/`,
    logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png',
    description: 'Especialistas em compras internacionais, agentes chineses e planilhas CSSBuy.',
    foundingDate: '2026',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      availableLanguage: ['Portuguese', 'English'],
    },
  },
  faqPage: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  },
  breadcrumb: {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: `${siteUrl}/`,
      },
    ],
  },
};

const howItWorksImages = [
  'https://readdy.ai/api/search-image?query=Young%20person%20browsing%20fashion%20categories%20on%20smartphone%20with%20colorful%20product%20grid%20visible%20on%20screen%2C%20cozy%20coffee%20shop%20background%2C%20warm%20afternoon%20light%2C%20casual%20modern%20lifestyle%2C%20Brazilian%20cafe%20atmosphere%20with%20tropical%20plants%2C%20relaxed%20browsing%20moment%2C%20authentic%20candid%20smartphone%20photography%2C%20soft%20bokeh%20background%2C%20warm%20amber%20and%20green%20tones&width=400&height=500&seq=hiw-v2-step1&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Laptop%20screen%20displaying%20organized%20product%20spreadsheet%20with%20fashion%20item%20thumbnails%2C%20clean%20modern%20workspace%20with%20notebook%20and%20coffee%2C%20soft%20natural%20window%20light%2C%20productive%20morning%20atmosphere%2C%20Scandinavian%20desk%20setup%2C%20organized%20digital%20workflow%20scene%2C%20warm%20cream%20and%20wood%20tones%2C%20professional%20workspace%20photography%2C%20shallow%20depth%20of%20field%20on%20screen&width=400&height=500&seq=hiw-v2-step2&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Close%20up%20of%20hand%20copying%20product%20link%20from%20laptop%20screen%2C%20modern%20minimalist%20desk%20setting%2C%20warm%20desk%20lamp%20lighting%20in%20evening%20atmosphere%2C%20focused%20work%20moment%2C%20sleek%20wireless%20mouse%20and%20mechanical%20keyboard%20visible%2C%20cozy%20productive%20evening%20vibe%2C%20technology%20lifestyle%20photography%2C%20soft%20warm%20tungsten%20light%2C%20intimate%20focused%20composition&width=400&height=500&seq=hiw-v2-step3&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Person%20completing%20online%20purchase%20on%20tablet%20device%20with%20shopping%20cart%20icon%20visible%2C%20sitting%20on%20modern%20sofa%20in%20bright%20living%20room%2C%20natural%20daylight%20through%20large%20windows%2C%20comfortable%20home%20shopping%20experience%2C%20indoor%20plants%20and%20contemporary%20furniture%2C%20relaxed%20ecommerce%20moment%2C%20lifestyle%20technology%20photography%2C%20airy%20bright%20atmosphere%2C%20cream%20and%20sage%20interior&width=400&height=500&seq=hiw-v2-step4&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Happy%20person%20unboxing%20fashion%20package%20delivery%20at%20doorstep%2C%20genuine%20excitement%20expression%2C%20cardboard%20box%20with%20tissue%20paper%2C%20sunny%20morning%20light%2C%20modern%20apartment%20entrance%20with%20plants%2C%20sustainable%20packaging%20visible%2C%20authentic%20unboxing%20joy%20moment%2C%20lifestyle%20delivery%20photography%2C%20warm%20golden%20morning%20tones%2C%20candid%20authentic%20emotion&width=400&height=500&seq=hiw-v2-step5&orientation=portrait',
];

export default function Home() {
  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />
      <HeroSection />

      {/* How It Works - quick steps with images */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
            Como funciona?
          </h2>
          <p className="text-foreground-500 text-center text-sm max-w-xl mx-auto mb-10">
            Cinco passos simples para começar a usar o melhor cssbuy spreadsheet do Brasil.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
            {[
              { step: '1', title: 'Escolha uma categoria', icon: 'ri-grid-line' },
              { step: '2', title: 'Abra o spreadsheet', icon: 'ri-file-list-3-line' },
              { step: '3', title: 'Copie o link', icon: 'ri-link' },
              { step: '4', title: 'Compre pelo agente', icon: 'ri-shopping-cart-line' },
              { step: '5', title: 'Receba seu pedido', icon: 'ri-archive-line' },
            ].map((item, idx) => (
              <div key={item.step} className="flex flex-col items-center text-center gap-3 group cursor-default">
                <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden mb-1">
                  <img
                    src={howItWorksImages[idx]}
                    alt={`Passo ${item.step}: ${item.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="w-7 h-7 rounded-full bg-background-50 text-foreground-950 flex items-center justify-center font-bold text-xs mb-1.5">
                      {item.step}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-foreground-300 text-xs font-semibold block mb-1">
                    Passo {item.step}
                  </span>
                  <p className="text-foreground-700 text-sm font-medium">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
            Por que usar nosso CSSBuy Spreadsheet?
          </h2>
          <p className="text-foreground-500 text-center max-w-2xl mx-auto mb-10 text-sm md:text-base">
            O cssbuy spreadsheet mais confiável do Brasil, feito para compradores que valorizam qualidade, segurança e economia.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {[
              { icon: 'ri-shield-check-line', title: 'Links verificados', desc: 'Todos os links são checados manualmente pela nossa equipe.' },
              { icon: 'ri-folders-line', title: 'Produtos organizados', desc: '40+ categorias para você encontrar tudo rapidamente.' },
              { icon: 'ri-refresh-line', title: 'Atualizações frequentes', desc: 'Novos produtos adicionados diariamente.' },
              { icon: 'ri-book-open-line', title: 'Guias completos', desc: 'Tutoriais e dicas para comprar com confiança.' },
              { icon: 'ri-lock-line', title: 'Dicas de segurança', desc: 'Proteja-se contra golpes e fraudes online.' },
              { icon: 'ri-scales-line', title: 'Comparações', desc: 'Compare agentes e escolha o melhor para você.' },
            ].map((benefit, i) => (
              <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6 flex flex-col items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className={`${benefit.icon} text-accent-600 text-lg`}></i>
                </div>
                <h3 className="text-foreground-900 font-semibold text-sm md:text-base">{benefit.title}</h3>
                <p className="text-foreground-500 text-xs md:text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CategoriesGrid />
      <ComparisonSection />
      <GuidesSection />
      <FAQSection />
      <AboutSection />
      <CTASection />
      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchemas.website) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchemas.organization) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchemas.faqPage) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchemas.breadcrumb) }}
      />
    </main>
  );
}